import {
  Condition,
  NumReturnsOption,
} from "@redotech/redo-model/return-flow/condition";
import * as React from "react";
import { ConditionType, ConditionTypeDetailsProps } from "../condition";
import {
  DateSubconditionState,
  emptyDateSubconditionState,
  fromModelDateSubcondition,
  getDateSubconditionSuffix,
  toModelDateSubcondition,
  validateDateSubcondition,
} from "./date-subcondition";

export interface NumCustomerReturnsState {
  numReturns: string;
  returnOption: NumReturnsOption | null;
  dateSubcondition: DateSubconditionState;
}

export const NUM_CUSTOMER_RETURNS = (
  Details: React.ComponentType<
    ConditionTypeDetailsProps<NumCustomerReturnsState>
  >,
): ConditionType<NumCustomerReturnsState, Condition.NumCustomerReturns> => ({
  name: "Number of previous customer returns",
  description(state) {
    if (!state.returnOption) {
      return "The total number of returns this customer has made across all their orders";
    }

    let description = `Total customer returns across all orders ${state.returnOption.toLowerCase()} ${state.numReturns}`;

    // Add date subcondition suffix from shared helper
    description += getDateSubconditionSuffix(state.dateSubcondition);

    return description;
  },
  Details(props: ConditionTypeDetailsProps<NumCustomerReturnsState>) {
    return React.createElement(Details, props);
  },
  empty: {
    numReturns: (0).toFixed(0),
    returnOption: null,
    dateSubcondition: emptyDateSubconditionState,
  },
  fromModel(model) {
    return {
      numReturns: model.numReturns.toFixed(0),
      returnOption: model.numReturnsOption,
      // Use dateSubcondition directly
      dateSubcondition: model.dateSubcondition
        ? fromModelDateSubcondition(model.dateSubcondition)
        : emptyDateSubconditionState,
    };
  },
  toModel(state) {
    return {
      type: Condition.NUM_CUSTOMER_RETURNS,
      numReturns: +state.numReturns,
      numReturnsOption: state.returnOption,
      // Use only dateSubcondition going forward
      dateSubcondition: toModelDateSubcondition(state.dateSubcondition),
    };
  },
  valid(state) {
    const returnConditionValid =
      state.returnOption !== null &&
      +state.numReturns >= 0 &&
      +state.numReturns % 1 === 0; // Must be whole number

    // Validate date subcondition
    const dateSubconditionValid = validateDateSubcondition(
      state.dateSubcondition,
    );

    return returnConditionValid && dateSubconditionValid;
  },
});
